import { useCallback, useEffect, useMemo } from "react";
import {
  useQuery,
  useQueryClient,
  type Query,
  type QueryKey
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { liveViewApi, liveViewTicketKey } from "api/live-view";
import { getErrorMessage } from "utils/error";

/** Both ticket kinds: `{ enabled: false }`, or a token with its lifetime. */
type Ticket =
  | { enabled: false }
  | { enabled: true; token: string; expires_in: number };

/** Re-mint this long before the ticket expires. */
const TICKET_REFRESH_MARGIN_MS = 60_000;
const MIN_REFRESH_MS = 30_000;

const ticketLifetimeMs = (ticket: Ticket | undefined) =>
  ticket?.enabled
    ? Math.max(
        ticket.expires_in * 1000 - TICKET_REFRESH_MARGIN_MS,
        MIN_REFRESH_MS
      )
    : null;

/**
 * A ticket for the `/live-view` socket, refreshed before it expires.
 * `getFreshToken` serves the socket handshake, which runs again on every
 * reconnect; `force` re-mints even if the cached one looks fresh (e.g. after
 * the server rejected it or dropped the socket at expiry). Pass a memoized
 * `queryKey` and `queryFn`: `getFreshToken` changes when they do.
 */
export const useTicketQuery = <T extends Ticket>({
  queryKey,
  queryFn,
  enabled,
  errorToastId
}: {
  queryKey: QueryKey;
  queryFn: () => Promise<T>;
  enabled: boolean;
  errorToastId: string;
}) => {
  const queryClient = useQueryClient();

  const ticket = useQuery({
    queryKey,
    queryFn,
    enabled,
    // Tickets are refreshed on a timer instead of on focus/mount.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: (query: Query<T>) =>
      ticketLifetimeMs(query.state.data) ?? false,
    retry: 1
  });

  useEffect(() => {
    if (!ticket.error) return;
    toast.error(`Live view is unavailable: ${getErrorMessage(ticket.error)}`, {
      toastId: errorToastId
    });
  }, [ticket.error, ticket.errorUpdatedAt, errorToastId]);

  const getFreshToken = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      const state = queryClient.getQueryState<T>(queryKey);
      const cached = state?.data;
      const lifetime = ticketLifetimeMs(cached);
      const isCachedFresh =
        !force &&
        lifetime !== null &&
        Date.now() - (state?.dataUpdatedAt ?? 0) < lifetime;

      const fresh =
        cached && isCachedFresh
          ? cached
          : await queryClient.fetchQuery({ queryKey, queryFn, staleTime: 0 });

      return fresh.enabled ? fresh.token : null;
    },
    [queryKey, queryFn, queryClient]
  );

  return { data: ticket.data, getFreshToken };
};

/** The watch ticket for one application page. */
export const useLiveViewTicket = (applicationId: string | undefined) => {
  const id = applicationId || "";
  const queryKey = useMemo(() => liveViewTicketKey(id), [id]);
  const queryFn = useCallback(() => liveViewApi.createTicket(id), [id]);
  const { data, getFreshToken } = useTicketQuery({
    queryKey,
    queryFn,
    enabled: Boolean(applicationId),
    errorToastId: "live-view-ticket"
  });

  return { enabled: data?.enabled === true, getFreshToken };
};
